import { useState, useRef, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { startPoll } from "@/lib/poll";
import { formatApiError, limitWords, countWords } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useCountdown } from "@/lib/useCountdown";
import { DnaHelix } from "@/components/DnaHelix";
import { DelegateConfirmModal } from "@/components/undertake/DelegateConfirmModal";
import { MyDelegations } from "@/components/undertake/MyDelegations";
import { toast } from "sonner";

const DRAFT_KEY = "mawilu_delegate_draft";
const MAX = 77;

export function DelegateTab() {
  const { user, refreshUser } = useAuth();
  const [text, setText] = useState(() => localStorage.getItem(DRAFT_KEY) || "");
  const [focused, setFocused] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [quota, setQuota] = useState(null);
  const [mine, setMine] = useState([]);
  const ref = useRef(null);

  const loadQuota = useCallback(() => api.get("/undertakings/quota").then(({ data }) => setQuota(data)).catch((e) => console.debug("quota", e)), []);
  const loadMine = useCallback(() => api.get("/undertakings/mine").then(({ data }) => setMine(data)).catch((e) => console.debug("mine", e)), []);
  useEffect(() => { loadQuota(); }, [loadQuota]);
  useEffect(() => { loadMine(); return startPoll(loadMine, 8000); }, [loadMine]);

  const count = quota?.count ?? 0;
  const limit = quota?.limit ?? 1;
  const done = count >= limit;
  const remaining = useCountdown(done ? quota?.reset_at : null, loadQuota);

  useEffect(() => {
    if (text.trim()) localStorage.setItem(DRAFT_KEY, text);
    else localStorage.removeItem(DRAFT_KEY);
  }, [text]);

  useEffect(() => {
    if (ref.current) { ref.current.style.height = "auto"; ref.current.style.height = ref.current.scrollHeight + "px"; }
  }, [text]);

  const attempt = () => {
    if (!text.trim() || busy) return;
    if (user?.is_guest) { toast("Sign in"); return; }
    if (done) { toast("One delegation per day — come back when the clock runs out"); return; }
    setConfirming(true);
  };

  const confirm = async (provisions) => {
    setBusy(true);
    try {
      const { data } = await api.post("/undertakings", { text, provisions });
      setText("");
      localStorage.removeItem(DRAFT_KEY);
      setConfirming(false);
      setQuota(data.quota);
      refreshUser?.();
      loadMine();
      toast.success("delegated");
    } catch (err) {
      if (err.response?.status === 429) loadQuota();
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col items-center" style={{ marginTop: "6vh" }}>
      <div data-testid="delegate-quota" className="mb-10 text-2xl font-head font-light tracking-widest" style={{ color: "var(--text-primary)" }}>
        {count}/{limit}
      </div>
      <div className="relative w-full max-w-3xl">
        <textarea
          ref={ref}
          data-testid="delegate-input"
          value={text}
          onChange={(e) => setText(limitWords(e.target.value, text, MAX))}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); attempt(); } }}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          rows={1}
          disabled={done}
          className="w-full text-center text-2xl md:text-3xl font-head font-light resize-none overflow-hidden"
          style={{ background: "transparent", color: "var(--text-primary)", lineHeight: 1.5, pointerEvents: done ? "none" : undefined }}
        />
        {!focused && !text.trim() && (
          <div className="absolute inset-0 flex items-center justify-center fade-in" style={{ pointerEvents: "none" }} data-testid="delegate-helix-overlay">
            <DnaHelix />
          </div>
        )}
      </div>
      <div className="mt-4 h-4 text-xs tracking-widest" data-testid="delegate-word-count" style={{ color: countWords(text) >= MAX ? "var(--accent)" : "var(--text-muted)" }}>
        {text.trim() && `${countWords(text)} / ${MAX} words`}
      </div>
      <div className="mt-12 min-h-[1.5rem] w-full max-w-3xl flex justify-end">
        {text.trim() && !done && (
          <button onClick={attempt} disabled={busy} data-testid="delegate-submit-btn" className="text-xs tracking-widest fade-in" style={{ color: "var(--accent)" }}>
            press enter to delegate ↵
          </button>
        )}
      </div>
      {done && (
        <div className="text-center fade-in mt-4" data-testid="delegate-done">
          <div className="text-lg tracking-widest uppercase" style={{ color: "var(--accent)" }}>delegated for today</div>
          {remaining && <div className="text-sm tracking-widest mt-3" style={{ color: "var(--text-muted)" }} data-testid="delegate-clock">{remaining}</div>}
        </div>
      )}
      <MyDelegations items={mine} onChanged={() => { loadMine(); loadQuota(); }} />
      {confirming && <DelegateConfirmModal text={text} busy={busy} onCancel={() => setConfirming(false)} onConfirm={confirm} />}
    </div>
  );
}
